import React, {useMemo, useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {Alert, ButtonDropdownField, DropdownList, FlexColumn, FlexRow, Modal, SubHeading} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import type {ChangeExpenseCategoryModal_viewer$data as ChangeExpenseCategoryModal_viewer} from './__generated__/ChangeExpenseCategoryModal_viewer.graphql';
import {getNotNullishNodes} from '../../../forecast-app/shared/util/NotNullPredicate';
import BulkUpdateExpenseItemsMutation from '../../../mutations/bulk_update_expense_items_mutation';
import Util from '../../../forecast-app/shared/util/util';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import {ExpenseRow} from '../ExpenseTypes';

type ChangeExpenseCategoryModalProps = {
	closeModal: () => void;
	viewer: ChangeExpenseCategoryModal_viewer;
	selectedRows: ExpenseRow[];
};

type SelectedCategory = NonNullable<
	NonNullable<
		NonNullable<NonNullable<NonNullable<ChangeExpenseCategoryModal_viewer['company']>['expenseCategories']>['edges']>[0]
	>['node']
>;

const ChangeExpenseCategoryModal = ({closeModal, viewer, selectedRows}: ChangeExpenseCategoryModalProps) => {
	const {formatMessage} = useIntl();

	const countLockedRows = (selectedRows: ExpenseRow[]): number => {
		return selectedRows.filter(row => row.locked).length;
	};

	const lockedRowsCount = useMemo(() => countLockedRows(selectedRows), [selectedRows]);
	const categories = getNotNullishNodes(viewer?.company?.expenseCategories?.edges).filter(category => !category?.disabled);

	const [selectedCategory, setSelectedCategory] = useState<SelectedCategory>();

	const setSelected = (current: SelectedCategory) => {
		if (current) {
			setSelectedCategory(current);
		}
	};

	const handleBulkItemsCategoryChange = (expenseIds: Array<string>, category: SelectedCategory) => {
		const onSuccess = () => {
			createToast({duration: 5000, message: formatMessage({id: 'expense_category.has-been-updated'})});
		};
		Util.CommitMutation(
			BulkUpdateExpenseItemsMutation,
			{
				ids: expenseIds,
				expenseCategoryId: category.id,
			},
			onSuccess
		);
	};

	const confirmChanges = (selectedCategory: SelectedCategory | undefined) => {
		const expenseIds = selectedRows.filter(row => !row.locked).map(row => row.id);
		if (expenseIds && selectedCategory) {
			handleBulkItemsCategoryChange(expenseIds, selectedCategory);
		}
		closeModal();
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="expense_management.change_expense_category" />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'s'}>
					<FlexRow>
						<SubHeading>
							<FormattedMessage id={'expense_management.expense_category'} />
						</SubHeading>
					</FlexRow>
					<FlexRow>
						<FormattedMessage id={'expense_management.change_category_assign_long'} />
					</FlexRow>
					<FlexRow>
						<DropdownList>
							<DropdownList.Trigger>
								<ButtonDropdownField data-cy={'select-new-expense-category-dropdown'}>
									{!selectedCategory ? (
										<FormattedMessage id={'expense_management.change_category_assign'} />
									) : (
										selectedCategory.name
									)}
								</ButtonDropdownField>
							</DropdownList.Trigger>
							<DropdownList.Content align={'start'}>
								{categories.map(
									current =>
										current.name && (
											<DropdownList.Item onClick={() => setSelected(current)}>
												<FlexRow>{current.name}</FlexRow>
											</DropdownList.Item>
										)
								)}
							</DropdownList.Content>
						</DropdownList>
					</FlexRow>
				</FlexColumn>
				{lockedRowsCount > 0 && (
					<FlexRow>
						<Alert
							text={formatMessage(
								{id: 'expense_management.change_expense_category_alert'},
								{count: selectedRows.length, lockedCount: lockedRowsCount}
							)}
							type={Alert.TYPE.INFORMATION}
						/>
					</FlexRow>
				)}
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					disabled={!selectedCategory}
					onPress={() => {
						confirmChanges(selectedCategory);
					}}
					dataCy={'confirm-new-expense-category'}
				>
					<FormattedMessage id={'common.confirm_changes'} />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id={'common.cancel'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const ChangeExpenseCategoryModalQuery = graphql`
	query ChangeExpenseCategoryModalQuery {
		viewer {
			actualPersonId
			component(name: "ChangeExpenseCategoryModal")
			...ChangeExpenseCategoryModal_viewer
		}
	}
`;

export default createFragmentContainer(ChangeExpenseCategoryModal, {
	viewer: graphql`
		fragment ChangeExpenseCategoryModal_viewer on Viewer {
			id
			company {
				id
				expenseCategories(first: 10000) @connection(key: "Company_expenseCategories", filters: []) {
					edges {
						node {
							id
							name
							disabled
						}
					}
				}
			}
		}
	`,
});
